import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, computed, DestroyRef, ElementRef, EventEmitter, inject, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BOOKING_PAGE_VIEWS, BOOKING_REDIRECTION_VIEWS, CAPTCHA_TYPE, GTM_EVENTS, MEETING_TYPE_ID, PAYMENT_TYPES, SMART_WIDGET_APPOINTMENT_SOURCE_ID, VAT_TYPE } from '@constants/app.constants';
import { ADYEN_ERROR } from '@constants/adyen-error.constants';
import { ADYEN_RESUME } from '@constants/adyen-resume.constants';
import { DATE_ISO_FORMAT } from '@constants/date.constants';
import { BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS } from '@constants/local-storage.constants';
import { PaymentMethodViewComponent } from '@features/booking/components/payment-method-view/payment-method-view.component';
import { RelatedServiceSuggestionsComponent } from '@features/booking/components/related-service-suggestions/related-service-suggestions.component';
import { AppointmentBookingModel, BookedAppointment, BookingPageViews } from '@models/appointment-booking.model';
import { AppointmentServiceModel } from '@models/appointment-service.model';
import { BookingFailureKind, SpentAdyenSession } from '@models/booking-outcome.model';
import { AppointmentReservationBody } from '@models/appointment-reservation.model';
import { CouponDbModel } from '@models/coupon-db.model';
import { MeetingTypesModel } from '@models/meeting-types.model';
import { AppointmentCartItem } from '@models/selected-booking-data.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormatPipe } from '@pipes/address-format.pipe';
import { CalioCurrencyPipe } from '@pipes/calio-currency.pipe';
import { CalioDateUtcPipe } from '@pipes/calio-date-utc.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { AdyenResumeService } from '@services/feature/adyen-resume.service';
import { BookingService } from '@services/feature/booking.service';
import { adyenBookingRefusal, isAdyenAlreadyBooked } from '@services/utils/adyen-error.util';
import { classifyBookingFailure, offersPayAgain } from '@services/utils/booking-outcome.util';
import { resendWhileBookingLocked } from '@services/utils/booking-lock-resend.util';
import { courtesyKey } from '@services/utils/courtesy-key.util';
import { CustomEventService } from '@services/feature/custom-event.service';
import { TimezoneService } from '@services/feature/timezone.service';
import { WidgetService } from '@services/feature/widget.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { FeatureService } from '@services/utils/feature.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { LocalstorageService } from '@services/utils/localstorage.service';
import { LoggerService } from '@services/utils/logger.service';
import { TaxableLine, VatResult, calculateVat, isVatAddedOnTop, vatTypeLabelKey } from '@services/utils/vat-calculation.util';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import moment from 'moment';
import { finalize, identity, Subscription } from 'rxjs';

@Component({
  selector: 'app-booking-summary-view',
  templateUrl: './booking-summary-view.component.html',
  styleUrls: ['./booking-summary-view.component.scss'],
  imports: [
    CswTextComponent, PaymentMethodViewComponent, ButtonComponent, RelatedServiceSuggestionsComponent,
    AddressFormatPipe, CalioCurrencyPipe, CalioSafePipe, CalioTranslationPipe, CalioDateUtcPipe, CalioMeetingTemplatePipe, DatePipe,
    TranslateModule, NgTemplateOutlet,
  ]
})
export class BookingSummaryViewComponent implements OnInit, OnDestroy {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;

  private timezoneService = inject(TimezoneService);
  public translate = inject(TranslateService);
  public bookingService = inject(BookingService);
  private customEventService = inject(CustomEventService);
  public widgetService = inject(WidgetService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private featureService = inject(FeatureService);
  private dateUtilService = inject(DateUtilService);
  private localstorageService = inject(LocalstorageService);
  private adyenResumeService = inject(AdyenResumeService);
  private destroyRef = inject(DestroyRef);

  @Output() nextPageEvent = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();
  @Output() gotoPageEvent = new EventEmitter<any>();

  protected lang: string;
  protected subTotal = 0;
  protected totalAmount = 0;
  protected vat = signal<VatResult>({ byRate: [], totalCents: 0 });
  protected showCoupon = false;
  protected couponCode: string;
  protected finalCouponDiscount = 0;
  protected templateTitle: WidgetTemplateModel;
  protected templateSubTitle: WidgetTemplateModel;
  protected initialSmartWidgetView: string;
  protected selectedMeetingType: MeetingTypesModel;

  private couponFailedSubscription$: Subscription;
  private couponSuccessSubscription$: Subscription;
  private doBookingSubscription$: Subscription;
  protected readonly paymentTypes = PAYMENT_TYPES;
  protected readonly vatType = VAT_TYPE;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;
  protected readonly meetingTypeId = MEETING_TYPE_ID;
  protected storageComplete = false;
  protected readonly bookingErrorMessage = signal<string | undefined>(undefined);
  /**
   * How the last booking after an Adyen payment failed, while that decides what the summary offers:
   * `unknown` and `spent` keep the payment form down until "Pay again", `refused` replaces it with
   * "Choose another time", `completing` takes it away for good. Null while the payment form is live.
   */
  protected readonly adyenBookingFailure = signal<BookingFailureKind | null>(null);
  /**
   * A booking may stand behind the spent payment, so the customer checks their email first: the
   * payment form - and Book with it - stays down until they press "Pay again".
   */
  protected readonly payAgainRequired = computed(() => offersPayAgain(this.adyenBookingFailure()));
  /**
   * The Adyen payment of this booking already backs a booking - the backend's answer to a /book
   * carrying a session it had already claimed. Terminal: the payment form (method, coupon, Book) is
   * gone, so nothing can be paid or booked again for it; the customer checks their email.
   */
  protected readonly adyenAlreadyBooked = signal(false);
  protected readonly adyenAlreadyBookedKey = ADYEN_RESUME.I18N.ALREADY_BOOKED;
  /**
   * A page that died after sending /book is replaying that request. Only its answer may say "booked"
   * or "already booked"; until it arrives the summary says the payment is being checked.
   */
  protected readonly adyenReplaying = signal(false);
  protected readonly adyenCheckingKey = ADYEN_RESUME.I18N.CHECKING;
  /** Every Adyen session a failed booking spent in this checkout - each one is quoted to the customer. */
  protected readonly spentAdyenSessions = signal<SpentAdyenSession[]>([]);
  /** Refunded because the booking hit a technical failure. */
  protected readonly technicalRefundIds = computed(() => this.sessionIdsFailedAs('transient'));
  /**
   * Refunded because the backend refused THIS booking (slot taken, lead time, limits), or because the
   * card payment behind it was not accepted. Neither is a technical issue on our side.
   */
  protected readonly refusedRefundIds = computed(() => this.sessionIdsFailedAs('refused', 'declined'));
  /** The booking's answer was lost: it may exist, so no refund is promised. */
  protected readonly unconfirmedSessionIds = computed(() => this.sessionIdsFailedAs('unknown'));
  /** Still locked after every resend: the payment is taken and its booking is being completed. */
  protected readonly completingSessionIds = computed(() => this.sessionIdsFailedAs('completing'));
  /** The backend will not claim the session again: no refund is promised, a booking may stand behind it. */
  protected readonly spentSessionIds = computed(() => this.sessionIdsFailedAs('spent'));

  /** An Adyen authorisation (possibly a 3DS2 challenge) is running right now. */
  private readonly adyenAuthorising = signal(false);

  /** The refund notice for a refused slot: no "technical issue" sentence - nothing technical went wrong. */
  protected get refundNoticeRefusedKey(): string {
    return this.courtesyKey('payment_components.refundNoticeRefused');
  }

  protected get bookingOutcomeUnknownNoticeKey(): string {
    return this.courtesyKey('payment_components.bookingOutcomeUnknownNotice');
  }

  protected get bookingCompletingNoticeKey(): string {
    return this.courtesyKey('payment_components.bookingCompletingNotice');
  }

  protected get paymentSessionSpentNoticeKey(): string {
    return this.courtesyKey(ADYEN_ERROR.BOOKING_NOTICES.SESSION_SPENT);
  }

  get timeFormat(): string {
    return this.dateUtilService.userTimeFormat;
  }

  /**
   * True while the cart must not grow.
   *
   * Once an online payment has been authorised, adding a service makes the cart bigger than the
   * amount already charged - and a multi-item cart is not Adyen-payable, so the booking would go
   * through as pay-on-site with the card already charged. Freezing the cart is safer than
   * discarding an authorised payment.
   *
   * And while Adyen is merely SELECTED: one session pays one booking, so the moment a second
   * service lands in the cart Adyen stops being offerable, the Drop-in is destroyed underneath
   * the shopper (mid-authorisation, that discards an authorised result - money taken, no
   * booking), and Book has nothing left to submit to. Not offering the growth is the fix.
   */
  protected get cartFrozen(): boolean {
    const paid = this.bookingService.selectedBookingData?.paymentMethodData;

    return !!paid?.adyenPaymentDetails
      || !!paid?.paypalOrderDetail
      || paid?.paymentType === this.paymentTypes.ADYEN;
  }

  /**
   * True while nothing on the summary may change the amount.
   *
   * The Adyen session carries the amount it was minted for. Removing the coupon or a cart item
   * while it is authorising re-prices the booking underneath a payment that will be charged for
   * the OLD total, and after authorisation it strands that payment - the booking is then refused
   * for the mismatch. Narrower than cartFrozen on purpose: with Adyen merely selected the Drop-in
   * re-mints on a price change, so removing the coupon before paying stays allowed.
   *
   * A paid /book in flight (lock resends included) keeps the lock through adyenPaymentDetails. Its
   * answer clears that record, so the two outcomes that leave the payment taken hold the lock
   * themselves: `completing` (that /book is still booking it) and "already booked". Releasing the
   * cart there would let the shopper remove the paid slot, pick it again and pay a second time.
   */
  protected get paymentAttemptLocked(): boolean {
    return this.adyenAuthorising()
      || !!this.bookingService.selectedBookingData?.paymentMethodData?.adyenPaymentDetails
      || this.adyenBookingFailure() === 'completing'
      || this.adyenAlreadyBooked();
  }

  @ViewChild('bookingSummaryView') bookingSummaryView: ElementRef;

  constructor() {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
    this.initialSmartWidgetView = this.bookingService.initialSmartWidgetView;
  }

  ngOnInit(): void {
    this.couponSuccessSubscription$ = this.customEventService.couponSuccessEvent.subscribe({
      next: (result: CouponDbModel) => {
        this.bookingService.selectedBookingData.selectedCoupon = result;
        this.calculateCoupon(result);
        this.calculateTotalPrice();
        this.showCoupon = true;
        this.resetPaymentGatewayData();
      }
    });

    this.couponFailedSubscription$ = this.customEventService.couponFailedEvent.subscribe({
      next: (result: {
        disableBookingButton: boolean,
        invalidCoupon: boolean,
      }) => {
        if (this.bookingService.selectedBookingData.selectedCoupon) {
          this.removeCoupon();
        }
      }
    });

    this.doBookingSubscription$ = this.customEventService.doBookingEvent.subscribe({
      next: (result: any) => {
        this.book();
      }
    });

    this.customEventService.storeBookingItemsEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.storeBookingDataForStripeRedirect();
    });

    this.customEventService.resetStripeTokenEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.storageComplete = false;
    });

    this.customEventService.adyenPaymentStartedEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.adyenAuthorising.set(true));

    // An authorised payment keeps the lock through adyenPaymentDetails; a failed one releases it.
    this.customEventService.adyenSuccessfulEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.adyenAuthorising.set(false));

    this.customEventService.resetAdyenEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.adyenAuthorising.set(false));

    this.customEventService.adyenSubmittedResumeEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.takeSubmittedResume());
    this.takeSubmittedResume();

    // Survives this view: a customer sent back to choose another time still sees the earlier references.
    this.spentAdyenSessions.set([...(this.bookingService.selectedBookingData.paymentMethodData?.spentAdyenSessions ?? [])]);

    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_summary);
    this.calculateSubTotalPrice();
    // A booking restored after an Adyen payment left the page carries the coupon it was paid with;
    // the total must include it again, or the summary shows a price other than the one charged.
    if ((this.adyenResumeService.hasPendingResume() || this.adyenReplaying() || this.adyenAlreadyBooked())
      && this.bookingService.selectedBookingData.selectedCoupon) {
      this.calculateCoupon(this.bookingService.selectedBookingData.selectedCoupon);
      this.showCoupon = true;
    }
    this.calculateTotalPrice();
    this.setupTemplates();
  }

  calculateSubTotalPrice(): void {
    let subTotal = 0;

    for (const cartItem of this.bookingService.selectedBookingData.cartItems) {
      const price = cartItem?.service?.price || 0;

      if (price > 0) {
        subTotal += price;
      }
    }

    this.subTotal = subTotal;
    this.bookingService.selectedBookingData.subTotal = this.subTotal;
  }

  /**
   * Builds the taxable lines for the VAT calculation from the current cart: one line per
   * cart item with a price > 0 and a tax rate > 0. Addon custom-field-option prices are not
   * part of `cartItem.service.price` here and are correctly excluded — they carry no tax_id.
   */
  private buildTaxableLines(): TaxableLine[] {
    const lines: TaxableLine[] = [];

    for (const cartItem of this.bookingService.selectedBookingData.cartItems) {
      const price = cartItem?.service?.price || 0;
      const rate = cartItem?.service?.tax?.rate || 0;

      if (price > 0 && rate > 0) {
        lines.push({ netCents: Math.round(price * 100), rateHundredths: Math.round(rate * 100) });
      }
    }

    return lines;
  }

  /**
   * Per-item informational VAT amount shown next to the "plus 19% VAT" label — always the
   * item's own full VAT (ratio 1), independent of any coupon applied to the cart total.
   */
  protected itemVatAmount(cartItem: AppointmentCartItem): number {
    const price = cartItem?.service?.price || 0;
    const rate = cartItem?.service?.tax?.rate || 0;

    if (price <= 0 || rate <= 0) {
      return 0;
    }

    const netCents = Math.round(price * 100);
    const result = calculateVat([{ netCents, rateHundredths: Math.round(rate * 100) }], netCents, netCents);
    return result.totalCents / 100;
  }

  calculateTotalPrice(): void {
    const undiscountedNetCents = Math.round(this.subTotal * 100);
    const netCents = Math.max(0, this.finalCouponDiscount > 0
      ? Math.round((this.subTotal - this.finalCouponDiscount) * 100)
      : undiscountedNetCents);

    const addTax = isVatAddedOnTop(this.bookingService?.partnerData?.widget_vat_type);
    const lines = addTax ? this.buildTaxableLines() : [];
    const vatResult = calculateVat(lines, netCents, undiscountedNetCents);
    this.vat.set(vatResult);

    this.totalAmount = Number(((netCents + vatResult.totalCents) / 100).toFixed(2));
    this.bookingService.selectedBookingData.totalBookingAmount = this.totalAmount;
    this.bookingService.selectedBookingData.vatAmount = vatResult.totalCents / 100;
  }

  calculateCoupon(coupon: CouponDbModel): void {
    this.couponCode = coupon.code;
    if (coupon && coupon.is_fixed_amount === 1) {
      this.finalCouponDiscount = Number(coupon.fixed_amount);
    }

    if (coupon && coupon.is_percentage === 1) {
      this.finalCouponDiscount = Number((Number(this.subTotal) / 100 * coupon.percentage).toFixed(2));
    }

    this.bookingService.selectedBookingData.totalDiscount = this.finalCouponDiscount;
  }

  book(): void {
    // The spent-session references are deliberately kept: a second failure must not hide the first.
    this.bookingErrorMessage.set(undefined);
    this.adyenBookingFailure.set(null);
    const bookingItems = this.createBookingPayload();
    this.executeBooking(bookingItems);
  }

  /**
   * Creates the booking payload from current booking data.
   * This payload can be stored for Stripe redirect and reused after redirect.
   */
  createBookingPayload(): AppointmentBookingModel[] {
    const bookingItems: AppointmentBookingModel[] = [];

    for (const cartItem of this.bookingService.selectedBookingData.cartItems) {
      const bookingData: AppointmentBookingModel = {
        partnerId: this.bookingService.partnerData.id,
        services: cartItem.serviceId + '',
        worker_id: cartItem.workerId,
        worker_uuids: cartItem.workerUuids,
        ...(cartItem.service?.is_combi && cartItem.combiServiceWorkersMapping ? {
          combi_workers: cartItem.combiServiceWorkersMapping,
          worker_id: Number(Object.values(cartItem.combiServiceWorkersMapping)[0])
        } : {}),
        start: cartItem.startTime,
        end: cartItem.endTime,
        prename: this.bookingService.selectedBookingData.selectedUser.forename,
        gender: this.bookingService.selectedBookingData.selectedUser.gender,
        lastname: this.bookingService.selectedBookingData.selectedUser.lastName,
        email: this.bookingService.selectedBookingData.selectedUser.eMail,
        phone: this.bookingService.selectedBookingData.selectedUser.phone,
        mobile: this.bookingService.selectedBookingData.selectedUser.mobile,
        comment: this.bookingService.selectedBookingData.selectedUser.comment,
        customer_id: this.bookingService.selectedBookingData.selectedUser.customerId,
        company: this.bookingService.selectedBookingData.selectedUser?.company,
        company_name: this.bookingService.selectedBookingData.selectedUser?.company,
        title: this.bookingService.selectedBookingData.selectedUser?.title,
        country: this.bookingService.selectedBookingData.selectedUser?.country,
        customer_notification_preference: this.bookingService.selectedBookingData.selectedUser.customer_notification_preference,
        meeting_type_id: (cartItem.selectedMeetingTypeId || this.bookingService.selectedBookingData.selectedUser?.meeting_type_id),
        customFieldValues: this.bookingService.selectedBookingData.selectedCustomFieldValues,
        stripe_payment_intent_id: this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId ?? null,
        coupon_code: this.bookingService.selectedBookingData.selectedCoupon ? this.bookingService.selectedBookingData.selectedCoupon.code : null,
        payment_type: this.bookingService.selectedBookingData.paymentMethodData.paymentType,
        // only set for external_invoice; absent otherwise so every other payment type's request
        // stays exactly as it was
        ...(this.bookingService.selectedBookingData.paymentMethodData.invoiceAddress
          ? { invoice_address: this.bookingService.selectedBookingData.paymentMethodData.invoiceAddress }
          : {}),
        street: this.bookingService.selectedBookingData.selectedUser.street,
        zip: this.bookingService.selectedBookingData.selectedUser.zip,
        city: this.bookingService.selectedBookingData.selectedUser.city,
        captcha: this.bookingService.selectedBookingData.selectedUser?.captcha || undefined,
        friendly_captcha: this.bookingService.selectedBookingData.selectedUser?.friendly_captcha || undefined,
        token: this.bookingService.selectedBookingData?.captcha?.uuid || undefined,
        amount: this.bookingService.selectedBookingData?.totalDiscount,
        appointment_source_id: SMART_WIDGET_APPOINTMENT_SOURCE_ID,
        birthday: this.bookingService.selectedBookingData.selectedUser.birthday ? moment(this.bookingService.selectedBookingData.selectedUser.birthday).format(DATE_ISO_FORMAT) : undefined,
        ...this.bookingService.selectedBookingData.selectedUser.policy !== undefined && {
          accept_data_policy: +this.bookingService.selectedBookingData.selectedUser.policy
        },
        // send timezone to the API if timezone feature is enabled
        ...this.bookingService.partnerData?.is_timezone_enabled && {
          timezone: this.bookingService.effectiveDisplayTimezone
        }
      };

      if (this.bookingService.selectedBookingData.paymentMethodData.paymentType === this.paymentTypes.ADYEN) {
        // Opaque handles only — the backend re-reads the session from Adyen and
        // trusts that, never what the browser claims was paid.
        bookingData.adyen_session_id =
          this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails?.session_id;
        bookingData.adyen_session_result =
          this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails?.session_result;
      }

      if (this.bookingService.selectedBookingData.paymentMethodData.paymentType === this.paymentTypes.PAYPAL) {
        bookingData.paypal_order_id = this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail.paypal_order_id;
        bookingData.paypal_payer_id = this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail.paypal_payer_id;
        bookingData.paypal_status = this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail.paypal_status;
        bookingData.paypal_raw_data = this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail.paypal_raw_data;
        bookingData.paypal_capture_id = this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail.paypal_capture_id;
      }

      if (this.widgetService?.widgetConf?.context?.lang) {
        bookingData.user_browser_language = this.widgetService?.widgetConf?.context?.lang;
        bookingData.user_selected_language = this.widgetService?.widgetConf?.context?.lang;
      } else if (this.widgetService?.widgetConf && this.widgetService?.widgetConf?.lang) {
        bookingData.user_browser_language = this.widgetService?.widgetConf?.lang;
        bookingData.user_selected_language = this.widgetService?.widgetConf?.lang;
      } else if (this.widgetService?.widgetConf?.context?.locale) {
        bookingData.user_browser_language = this.widgetService?.widgetConf?.context?.locale;
        bookingData.user_selected_language = this.widgetService?.widgetConf?.context?.locale;
      } else if (this.widgetService?.widgetConf && this.widgetService?.widgetConf?.locale) {
        bookingData.user_browser_language = this.widgetService?.widgetConf?.locale;
        bookingData.user_selected_language = this.widgetService?.widgetConf?.locale;
      }

      this.widgetService?.widgetConf?.context?.utm_source && (bookingData.utm_source = this.widgetService.widgetConf.context.utm_source);
      this.widgetService?.widgetConf?.context?.utm_campaign && (bookingData.utm_campaign = this.widgetService.widgetConf.context.utm_campaign);
      this.widgetService?.widgetConf?.context?.utm_medium && (bookingData.utm_medium = this.widgetService.widgetConf.context.utm_medium);
      this.widgetService?.widgetConf?.context?.utm_content && (bookingData.utm_content = this.widgetService.widgetConf.context.utm_content);
      this.widgetService?.widgetConf?.context?.utm_term && (bookingData.utm_term = this.widgetService.widgetConf.context.utm_term);
      this.widgetService?.widgetConf?.context?.utm_id && (bookingData.utm_id = this.widgetService.widgetConf.context.utm_id);
      // Persisted on the appointment as its booking origin so workflow conditions can route on it.
      this.widgetService?.widgetConf?.widget_uuid && (bookingData.widget_uuid = this.widgetService.widgetConf.widget_uuid);
      bookingItems.push(bookingData);
    }

    return bookingItems;
  }

  /**
   * Executes the booking with the given payload.
   */
  executeBooking(bookingItems: AppointmentBookingModel[]): void {
    // Whatever method is selected now: this slot is already booked with the Adyen payment behind it.
    if (this.adyenAlreadyBooked()) {
      LoggerService.error(new Error('Booking refused on the summary: its Adyen payment already backs a booking'));
      return;
    }
    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking);
    this.bookingService.lastBookedAppointments = [];

    // A parked Adyen payment is being booked: a page that dies before the answer must, on its next
    // load, say "already booked" rather than take the payment again. Answered either way, the record
    // has done its job.
    const adyenSessionId = this.bookingService.selectedBookingData.paymentMethodData?.paymentType === this.paymentTypes.ADYEN
      ? this.bookingService.selectedBookingData.paymentMethodData?.adyenPaymentDetails?.session_id
      : undefined;
    this.adyenResumeService.markSubmitted(adyenSessionId, bookingItems, this.bookingService.selectedBookingData);

    this.bookingService.bookAppointments(bookingItems).pipe(
      // An Adyen-paid /book losing the booking lock may have lost it to this payment's own first
      // request, and cakephp refunds nothing on a lock: the SAME request goes again until the lock
      // has certainly expired, and books or answers "already booked". Never a new payment session.
      adyenSessionId ? resendWhileBookingLocked() : identity,
      // Kept when the answer is "already booked", or the booking is still being completed: the next
      // load must replay this /book or say "already booked", never offer to pay.
      finalize(() => {
        this.adyenReplaying.set(false);
        if (!this.adyenAlreadyBooked() && this.adyenBookingFailure() !== 'completing') {
          this.adyenResumeService.clearIfOwnedBy(adyenSessionId);
        }
      }),
    ).subscribe({
      next: (result: { success: boolean, errors: { message: string }[], appointments?: BookedAppointment[] }) => {
        if (result.success) {
          LoggerService.log('Booking completed');
          this.bookingService.lastBookedAppointments = result.appointments ?? [];
          this.bookingService.resetBookingDataAfterBooking();
          this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_SUCCESS_VIEW, isUserInteracted: true });

          if (this.featureService.hasFeature('is_external_success_page_forwarding_enabled')) {
            if (
              this.bookingService.redirectAfterBookingEnabled &&
              this.bookingService.successfulBookingRedirectUrl &&
              this.bookingService.redirectAfterBookingAction === BOOKING_REDIRECTION_VIEWS.BOOKING_SUMMARY_VIEW
            ) {
              this.bookingService.redirectToSuccessPage();
            }
          }
        } else {
          LoggerService.log('Booking failed');
          this.requireFreshCaptcha();
          // Refused without an HTTP error or a reason: after an authorised Adyen payment that session
          // is just as spent, so the customer stays here with the refund notice and a new payment form.
          if (this.releaseSpentAdyenPayment('transient')) {
            return;
          }
          this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW, isUserInteracted: true });
        }
      },
      error: (error: HttpErrorResponse) => {
        if (adyenSessionId && isAdyenAlreadyBooked(error)) {
          this.lockAsAlreadyBooked();
          return;
        }
        const errors = error?.error?.errors;
        // The status is tested BEFORE the message is read. The API's error renderer replaces
        // the message of every non-HttpException 5xx with Cake's own 'An Internal Error Has
        // Occurred.' - and every Adyen booking failure is one of those - so reading
        // errors[0].message first showed the customer developer text, untranslated, inline on
        // the summary. The widget's appointment flow has always tested the status first.
        const firstErrorMessage = error.status === HttpStatusCode.InternalServerError
          ? undefined
          : (errors?.[0]?.message || (errors && Object.values(errors)?.[0]?.['message']));
        this.requireFreshCaptcha();
        // A keyed Adyen refusal is shown in the customer's language, and its key - not the status -
        // decides what the summary offers next. An unknown key keeps the status-based handling.
        const refusal = adyenBookingRefusal(error);
        const refusalMessage = refusal
          ? this.translate.instant(refusal.courtesy ? this.courtesyKey(refusal.messageKey) : refusal.messageKey)
          : undefined;
        // A 200 carrying `errors` reaches here re-thrown by the interceptor, its message in an Error.
        const answeredMessage = refusalMessage
          ?? firstErrorMessage
          ?? (error?.error instanceof Error ? error.error.message : undefined);
        if (this.releaseSpentAdyenPayment(refusal?.failure ?? classifyBookingFailure(error), answeredMessage)) {
          return;
        }
        if (refusalMessage ?? firstErrorMessage) {
          this.bookingErrorMessage.set(refusalMessage ?? firstErrorMessage);
          this.customEventService.resetBookingButtonsEvent.emit();
          return;
        }
        this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_FAILED_VIEW, isUserInteracted: true });
        this.bookingErrorMessage.set(this.translatedBookingError());
        this.customEventService.resetStripeTokenEvent.emit();
      }
    });
  }

  /**
   * A booking that failed AFTER Adyen authorised the payment. Returns false when no authorised
   * Adyen payment was sent with it, so the caller's ordinary handling runs.
   *
   * That session is spent either way: cakephp claims it once, so re-sending it only ever earns
   * "already used". What the customer is offered depends on how the booking failed:
   * - `unknown` (the answer was lost): the booking may exist. No refund is promised and the payment
   *   form stays down - the customer checks their email and presses "Pay again" to re-arm it.
   * - `completing` (still locked after every resend): the payment is taken and the request holding
   *   the lock - most likely its own first one - is booking it. A neutral notice asks the customer to
   *   check their email; no error, no refund promise, and no "Pay again" or payment form at all.
   * - `refused` (the backend's verdict on this slot): the backend's own translated reason is shown
   *   with the refund notice, and the payment form is replaced by "Choose another time" - paying
   *   again for the same slot only earns the same answer.
   * - `transient` (the backend's own failure): the courtesy-aware "could not be booked" sentence and
   *   the refund notice, and the Drop-in re-arms here on a new session.
   * - `declined` (the card payment was not accepted): the refusal and the refund notice, and the
   *   Drop-in re-arms here on a new session - another card, or the same one again, can work.
   * - `spent` (the backend will not claim the session again): that is no proof a booking exists, so
   *   it is handled like a lost answer, in its own words. No red banner and no refund promise - an
   *   info notice quotes the session and asks the customer to check their email - and the payment
   *   form stays down until "Pay again".
   */
  private releaseSpentAdyenPayment(failure: BookingFailureKind, backendMessage?: string): boolean {
    const paymentData = this.bookingService.selectedBookingData.paymentMethodData;
    if (paymentData?.paymentType !== this.paymentTypes.ADYEN || !paymentData.adyenPaymentDetails) {
      return false;
    }

    const spent: SpentAdyenSession = { sessionId: paymentData.adyenPaymentDetails.session_id, failure };
    paymentData.adyenPaymentDetails = undefined;
    paymentData.spentAdyenSessions = [...(paymentData.spentAdyenSessions ?? []), spent];
    this.spentAdyenSessions.set([...paymentData.spentAdyenSessions]);
    this.adyenAuthorising.set(false);

    switch (failure) {
      case 'completing':
        this.adyenBookingFailure.set('completing');
        break;
      case 'unknown':
        this.adyenBookingFailure.set('unknown');
        this.bookingErrorMessage.set(this.translate.instant(this.courtesyKey('payment_components.bookingOutcomeUnknownError')));
        break;
      case 'refused':
        this.adyenBookingFailure.set('refused');
        this.bookingErrorMessage.set(backendMessage || this.translatedBookingError());
        break;
      case 'spent':
        // Not an error of the customer's: the info notice (spentSessionIds) says it all.
        this.adyenBookingFailure.set('spent');
        break;
      case 'declined':
        this.bookingErrorMessage.set(backendMessage || this.translatedBookingError());
        this.customEventService.adyenCheckoutResetRequested.emit();
        break;
      default:
        this.bookingErrorMessage.set(this.translatedBookingError());
        this.customEventService.adyenCheckoutResetRequested.emit();
    }
    this.customEventService.resetBookingButtonsEvent.emit();

    return true;
  }

  /**
   * A restored booking whose payment was already sent to /book: taken in ngOnInit, before the payment
   * form renders, so no payment pane mounts for it at all. The page cannot know whether that request
   * arrived, so it sends it again: the backend claims a session once, and answers either with the
   * booking (the first request was lost) or with "already booked" - the only proof shown.
   */
  private takeSubmittedResume(): void {
    const resume = this.adyenResumeService.peekPendingResume();
    if (!resume?.bookingSubmitted) {
      return;
    }
    this.adyenResumeService.takePendingResume();

    const bookingItems = resume.record.submittedBooking ?? [];
    const paymentData = this.bookingService.selectedBookingData.paymentMethodData;
    paymentData.paymentType = this.paymentTypes.ADYEN;
    paymentData.adyenPaymentDetails = {
      session_id: resume.record.sessionId,
      session_result: bookingItems[0]?.adyen_session_result ?? '',
    };
    this.adyenReplaying.set(true);
    this.executeBooking(bookingItems);
  }

  /** Locks the summary for a payment that already backs a booking. */
  private lockAsAlreadyBooked(): void {
    const paymentData = this.bookingService.selectedBookingData.paymentMethodData;
    if (paymentData) {
      paymentData.adyenPaymentDetails = undefined;
    }
    this.adyenAuthorising.set(false);
    this.adyenBookingFailure.set(null);
    this.bookingErrorMessage.set(undefined);
    this.adyenAlreadyBooked.set(true);
  }

  /** "Pay again" after a lost answer or a spent session: the customer has checked their email and re-arms the form. */
  protected payAgain(): void {
    this.adyenBookingFailure.set(null);
    this.bookingErrorMessage.set(undefined);
  }

  /**
   * "Choose another time" after the backend refused this slot. The refused slot leaves the cart
   * (and its reservation is released), so the slot step starts clean and one new slot keeps the
   * checkout Adyen-payable; the customer then passes the personal form again, which also collects
   * the fresh captcha answer the next booking needs.
   */
  protected chooseAnotherTime(): void {
    const cartItems = this.bookingService.selectedBookingData.cartItems ?? [];
    if (Number(this.bookingService.partnerData?.is_appointment_reservation_enabled) === 1) {
      cartItems.filter(item => item?.isAppointmentReserved).forEach(item => this.removeReservedAppointment(item));
    }
    this.bookingService.selectedBookingData.cartItems = [];
    this.bookingService.setAppointmentCartItemsInLocalStorage([]);
    this.customEventService.refreshCalendarBadgeInBookingEvent.emit({ cartItems: [] });
    this.adyenBookingFailure.set(null);
    this.bookingErrorMessage.set(undefined);
    this.gotoPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
  }

  /**
   * A refused booking has spent its Friendly Captcha answer (siteverify accepts each one once), so
   * it is cleared and the payment step asks for a fresh one before the next submit. The default
   * Calenso captcha is not touched.
   */
  private requireFreshCaptcha(): void {
    const partner = this.bookingService.partnerData;
    if (!Number(partner?.is_captcha_feature_enabled) || partner?.captcha_type !== CAPTCHA_TYPE.FRIENDLY) {
      return;
    }
    const user = this.bookingService.selectedBookingData?.selectedUser;
    if (user) {
      user.friendly_captcha = '';
    }
    this.customEventService.friendlyCaptchaRenewalRequested.emit();
  }

  private sessionIdsFailedAs(...failures: BookingFailureKind[]): string {
    return this.spentAdyenSessions()
      .filter(session => failures.includes(session.failure))
      .map(session => session.sessionId)
      .join(', ');
  }

  /** `<key>Du` or `<key>Sie`, by the partner's courtesy form. */
  private courtesyKey(key: string): string {
    return courtesyKey(key, this.widgetService.widgetConf?.partner?.courtesy_form);
  }

  /**
   * The same sentence the failed view shows, chosen by the partner's courtesy form. This repo has no
   * `common.*` namespace - the widget's `common.noDateError` key rendered here as the literal string.
   */
  private translatedBookingError(): string {
    return this.translate.instant(this.courtesyKey('booking_components.errorMessage'));
  }

  /**
   * Store booking payload in localStorage before Stripe redirect (for 3DS/SCA verification).
   * This payload will be used directly after redirect to complete the booking.
   */
  private storeBookingDataForStripeRedirect(): void {
    if (this.bookingService.selectedBookingData.paymentMethodData.paymentType === this.paymentTypes.CREDITCARD
      && !this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId) {
      LoggerService.log('[Stripe Redirect] Storing booking payload before Stripe verification');

      // Create the booking payload that will be used directly after redirect
      const bookingPayload = this.createBookingPayload();

      const redirectData = {
        // Pre-built booking payload - used directly after redirect
        bookingPayload,
        // Selected booking data - for UI restoration if needed
        selectedBookingData: {
          cartItems: this.bookingService.selectedBookingData.cartItems,
          selectedUser: this.bookingService.selectedBookingData.selectedUser,
          selectedCustomFieldValues: this.bookingService.selectedBookingData.selectedCustomFieldValues,
          selectedCoupon: this.bookingService.selectedBookingData.selectedCoupon,
          paymentMethodData: {
            paymentType: this.bookingService.selectedBookingData.paymentMethodData.paymentType
          },
          totalBookingAmount: this.bookingService.selectedBookingData.totalBookingAmount,
          totalDiscount: this.bookingService.selectedBookingData.totalDiscount,
          subTotal: this.bookingService.selectedBookingData.subTotal,
          vatAmount: this.bookingService.selectedBookingData.vatAmount,
          timezone: this.bookingService.selectedBookingData.timezone,
          captcha: this.bookingService.selectedBookingData.captcha,
          currentSelectedAppointmentState: this.bookingService.selectedBookingData.currentSelectedAppointmentState
        },
        partnerData: this.bookingService.partnerData
      };

      this.localstorageService.setItem(
        BOOKING_WIDGET_LOCAL_STORAGE_CONSTANTS.BOOKING_STRIPE_REDIRECTION_DATA,
        JSON.stringify(redirectData)
      );

      // Set storageComplete to true to trigger the payment confirmation in Stripe component
      this.storageComplete = true;
    }
  }

  previous(): void {
    this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.PERSONAL_FORM_VIEW, isUserInteracted: true });
  }

  onAdditionalService(): void {
    // @TODO(Pratik): Have to rectify this code once we have setting for clearning context on add additional service

    // Resetting state values as user is booking another appointment
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate = undefined;

    if (true) {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = undefined;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = undefined;
      this.widgetService.widgetConf.context.worker_email = undefined;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = [];
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices = [];
      // Reset isCombiServiceSelected flag when services are cleared
      this.bookingService.isCombiServiceSelected = false;
    } else {
      if (this.widgetService?.widgetConf?.context?.store_id === undefined) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = undefined;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = undefined;
      }

      if (
        this.widgetService?.widgetConf?.context?.appointment_service_ids === undefined ||
        this.widgetService?.widgetConf?.context?.appointment_service_ids?.length === 0
      ) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = [];
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices = [];
        // Reset isCombiServiceSelected flag when services are cleared
        this.bookingService.isCombiServiceSelected = false;
      }

      if (
        this.widgetService?.widgetConf?.context?.worker_id === undefined ||
        this.widgetService?.widgetConf?.context?.worker_email === undefined ||
        this.widgetService?.widgetConf?.context?.worker_uuid === undefined
      ) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = undefined;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = undefined;
      }

      if (this.widgetService?.widgetConf?.context?.store_id === undefined) {
        this.gotoPageEvent.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted: true });
      } else if (
        this.widgetService?.widgetConf?.context?.appointment_service_ids === undefined ||
        this.widgetService?.widgetConf?.context?.appointment_service_ids?.length === 0
      ) {
        this.gotoPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted: true });
      } else if (this.widgetService?.widgetConf?.context?.worker_id === undefined) {
        this.gotoPageEvent.emit({ view: BOOKING_PAGE_VIEWS.WORKERS_VIEW, isUserInteracted: true });
      } else {
        this.gotoPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
      }
    }

    let redirectToView: BookingPageViews = BOOKING_PAGE_VIEWS.SERVICES_VIEW;
    // [hidden_booking_steps] A hidden MEETING_TYPE step has no picker to return to: its forced
    // provider stays selected and the customer continues at the services step. Re-entering the
    // hidden step here would read as backward navigation (lastView is still SLOTS) and either
    // chain to the pre-booking questions or un-hide the step for the rest of the session.
    // Once the step has FAILED to resolve it renders normally, so it is a real reachable step
    // again and must be re-offered like any visible picker (same rule as isHiddenStepSuppressed).
    if (
      this.bookingService.isMeetingTypeStepInFlow() &&
      (
        !this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW) ||
        this.bookingService.hiddenStepResolutionFailed.has(BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW)
      )
    ) {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = undefined;
      redirectToView = BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW;
    }
    // update state in local storage
    this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);

    this.gotoPageEvent.emit({ view: redirectToView, isUserInteracted: true });
  }

  onAddRelatedService(event: {
    cartItem: AppointmentCartItem;
    slot: { start: string; end: string; shortStart: string; startHour: number; worker: any; workerUuids: string[]; store: any; combiServiceWorkersMapping: Record<number, number> };
    relatedService: AppointmentServiceModel;
  }): void {
    const newCartItem = new AppointmentCartItem();
    const dateString = this.dateUtilService.getFormattedDateByFormat(event.cartItem.date, DATE_ISO_FORMAT);

    // Generate cart item ID (pattern from slots-card-selection.component.ts:268-283)
    const workerIdentifier = event.slot.combiServiceWorkersMapping && Object.keys(event.slot.combiServiceWorkersMapping).length
      ? Object.values(event.slot.combiServiceWorkersMapping).join('_')
      : event.slot.worker.id;
    newCartItem.cartItemId = `${event.cartItem.storeId}-${workerIdentifier}-${event.relatedService.id}-${event.relatedService.meeting_provider_id}-${dateString}-${event.slot.shortStart}`;

    newCartItem.storeId = event.cartItem.storeId;
    newCartItem.store = event.slot.store || event.cartItem.store;
    newCartItem.date = event.cartItem.date;
    newCartItem.startTime = event.slot.start;
    newCartItem.endTime = event.slot.end;
    newCartItem.shortStart = event.slot.shortStart;
    newCartItem.startHour = event.slot.startHour;
    newCartItem.workerId = event.slot.worker.id;
    newCartItem.worker = event.slot.worker;
    newCartItem.workerUuids = event.slot.workerUuids;
    newCartItem.combiServiceWorkersMapping = event.slot.combiServiceWorkersMapping || {};
    newCartItem.serviceId = event.relatedService.id;
    newCartItem.service = event.relatedService;
    newCartItem.selectedMeetingTypeId = event.relatedService.meeting_provider_id;
    newCartItem.isAppointmentReserved = false;
    newCartItem.appointmentDaoObject = null;

    this.bookingService.selectedBookingData.cartItems.push(newCartItem);
    this.bookingService.setAppointmentCartItemsInLocalStorage(
      this.bookingService.selectedBookingData.cartItems
    );

    // Recalculate prices
    this.calculateSubTotalPrice();
    if (this.bookingService.selectedBookingData.selectedCoupon) {
      this.calculateCoupon(this.bookingService.selectedBookingData.selectedCoupon);
    }
    this.calculateTotalPrice();
    this.resetPaymentGatewayData();

    // Reserve related service appointment if reservation is enabled
    if (Number(this.bookingService.partnerData.is_appointment_reservation_enabled) === 1) {
      const reservationBody: AppointmentReservationBody = {
        appointment_service_id: event.relatedService.id,
        worker_id: event.slot.worker.id,
        start_time: event.slot.start,
        end_time: event.slot.end,
      };

      if (this.bookingService.partnerData?.is_timezone_enabled) {
        reservationBody.timezone = this.bookingService.effectiveDisplayTimezone;
      }

      this.bookingService.reserveAppointments([reservationBody]).subscribe({
        next: (reservation) => {
          if (reservation?.success) {
            newCartItem.isAppointmentReserved = true;
          }
        },
        error: (error) => {
          LoggerService.warn('[Debug] Failed to reserve related service appointment');
          LoggerService.error(error);
        }
      });
    }
  }

  removeCartItem(index: number): void {
    // The link is hidden while locked; this also stops a keyboard press on a stale focus.
    if (this.paymentAttemptLocked) {
      return;
    }
    // Remove reserved appointment when setting is enabled
    if (Number(this.bookingService.partnerData.is_appointment_reservation_enabled) === 1) {
      const appointment = this.bookingService.selectedBookingData.cartItems[index];
      appointment?.isAppointmentReserved && this.removeReservedAppointment(this.bookingService.selectedBookingData.cartItems[index]);
    }

    this.bookingService.selectedBookingData.cartItems.splice(index, 1);
    this.calculateSubTotalPrice();

    if (this.bookingService.selectedBookingData.cartItems?.length === 0) {
      this.bookingService.resetBookingDataAfterBooking();
      this.gotoPageEvent.emit({ view: this.initialSmartWidgetView, isUserInteracted: true });
    }

    if (this.bookingService.selectedBookingData.selectedCoupon) {
      this.calculateCoupon(this.bookingService.selectedBookingData.selectedCoupon);
    }

    this.calculateTotalPrice();
    this.resetPaymentGatewayData();
  }

  private removeReservedAppointment(appointment: AppointmentCartItem): void {
    const appointmentBody: AppointmentReservationBody = {
      appointment_service_id: appointment.serviceId,
      worker_id: appointment.workerId,
      start_time: appointment.startTime,
      end_time: appointment.endTime
    };

    this.bookingService.removeReservedAppointment([appointmentBody]).subscribe({
      error: (error: HttpErrorResponse) => {
        LoggerService.warn('[Debug] Failed to remove a reserved appointment');
        LoggerService.error(error);
      }
    });
  }

  removeCoupon(): void {
    if (this.paymentAttemptLocked) {
      return;
    }
    this.couponCode = undefined;
    this.bookingService.selectedBookingData.selectedCoupon = undefined;
    this.bookingService.selectedBookingData.totalDiscount = 0;
    this.finalCouponDiscount = 0;
    this.calculateTotalPrice();
    this.showCoupon = false;
    this.resetPaymentGatewayData();
    this.customEventService.removeCouponEvent.emit();
  }

  resetPaymentGatewayData(): void {
    if (this.bookingService.selectedBookingData.paymentMethodData.paymentType === this.paymentTypes.PAYPAL) {
      this.customEventService.resetPaypalEvent.emit();
    }
  }

  setupTemplates(): void {
    this.templateTitle = this.bookingService.widgetTemplates.find((template: WidgetTemplateModel) => {
      return template.id === 124;
    });
    if (this.templateTitle) {
      this.templateTitle.is_multi_language = 1;
    }
    this.templateSubTitle = this.bookingService.widgetTemplates.find((template: WidgetTemplateModel) => {
      return template.id === 125;
    });
    if (this.templateSubTitle) {
      this.templateSubTitle.is_multi_language = 1;
    }
  }

  ngOnDestroy(): void {
    if (this.doBookingSubscription$) {
      this.doBookingSubscription$.unsubscribe();
    }
    if (this.couponSuccessSubscription$) {
      this.couponSuccessSubscription$.unsubscribe();
    }
    if (this.couponFailedSubscription$) {
      this.couponFailedSubscription$.unsubscribe();
    }
  }
}

